/**
 * Saude de uma empresa: nivel, nota, achados e passos de implantacao.
 *
 * Funcao PURA sobre fatos que o servidor ja manda (status, prontidao,
 * metricas atuais e anteriores). Nenhuma chamada nova de rede: a lista de
 * centenas de empresas diagnostica todas com o mesmo `CompanySetup` que ja
 * veio na visao geral.
 *
 * Decisoes nao obvias:
 *  1. O NIVEL segue o status antes da nota: conta pausada e "Pausada" e conta
 *     nova e "Implantação", mesmo com nota alta. Chamar de "Crítica" uma
 *     empresa que ainda nao terminou de colar o webhook assustaria sem motivo;
 *     o que ela precisa e da lista de passos.
 *  2. Critico = a regua esta PERDENDO lead agora (evento recusado com 401,
 *     WhatsApp fora, nenhum provedor). Atencao = algo esta freando a
 *     recuperacao, mas venda ainda entra. Neutro = oportunidade ou pendencia
 *     administrativa, que nao derruba a nota.
 *  3. Modelo de cobranca "a definir" NAO e achado de saude: e uma decisao
 *     pendente do dono para todas as empresas, nao um defeito desta.
 *  4. Todo texto fala do FATO ja ocorrido (abandono, assinatura vencida).
 *     Nao existe achado do tipo "vai vencer": sem o fato nao ha comissao.
 */

import { AGORA_SIMULADO, MOCK_FORCED } from '../../lib/api';
import { formatCount, formatPercentFromBps } from '../../lib/format';
import type { CompanySummary } from '../../types';
import { descreverDias, diasDesde, variar } from './comparar';
import type { AchadoDeSaude, CompanyPeriodMetrics, CompanySetup, NivelSaude, PassoDeImplantacao, SaudeDaEmpresa, TomDoAchado } from './types';
import { PROVEDOR_INFO } from './vocabulario';

/** Dias sem webhook a partir dos quais uma empresa ativa vira "integracao muda". */
export const DIAS_INTEGRACAO_MUDA = 2;
/** Queda relativa do recuperado que vira achado (em bps: 2000 = 20%). */
const QUEDA_RELEVANTE_BPS = -2_000;
/** Alta relativa do recuperado que vira achado positivo. */
const ALTA_RELEVANTE_BPS = 1_000;
/** Queda da taxa, em pontos-base, que vira achado (300 = 3 pontos). */
const QUEDA_DE_TAXA_BPS = 300;

const PESO: Readonly<Record<TomDoAchado, number>> = { critical: 30, warning: 12, neutral: 0, good: 0 };
const ORDEM: Readonly<Record<TomDoAchado, number>> = { critical: 0, warning: 1, neutral: 2, good: 3 };

/** O "agora" da tela: fixo na previa (os eventos simulados sao relativos a ele), relogio real em producao. */
export function agoraDaTela(): Date {
  return MOCK_FORCED ? AGORA_SIMULADO : new Date();
}

function listaDeProvedores(lista: CompanySetup['providersMissingSecret']): string {
  const nomes = lista.map((p) => PROVEDOR_INFO[p].curto);
  if (nomes.length <= 1) {
    return nomes[0] ?? '';
  }
  return `${nomes.slice(0, -1).join(', ')} e ${nomes[nomes.length - 1] ?? ''}`;
}

export function passosDeImplantacao(setup: CompanySetup): readonly PassoDeImplantacao[] {
  const conectados = setup.connectedProviders.length > 0;
  return [
    { id: 'provedor', rotulo: 'Provedor de checkout conectado', feito: conectados, aba: 'integracoes' },
    { id: 'segredo', rotulo: 'Segredo de assinatura em todos os provedores', feito: conectados && setup.providersMissingSecret.length === 0, aba: 'integracoes' },
    { id: 'whatsapp', rotulo: 'WhatsApp conectado na Evolution', feito: setup.whatsapp === 'connected', aba: 'integracoes' },
    { id: 'voz', rotulo: 'Assistente de voz pronto para ligar', feito: setup.voice === 'ready', aba: 'integracoes' },
    { id: 'webhook', rotulo: 'Primeiro checkout abandonado recebido', feito: setup.lastWebhookAt !== null, aba: 'integracoes' },
    { id: 'vendedora', rotulo: 'Vendedora cadastrada para as respostas da régua', feito: setup.salesReps > 0, aba: 'equipe' },
  ];
}

export function diagnosticar(
  company: Pick<CompanySummary, 'status' | 'funnels'>,
  setup: CompanySetup,
  metrics: CompanyPeriodMetrics,
  previous: CompanyPeriodMetrics | null,
  agora: Date,
): SaudeDaEmpresa {
  const achados: AchadoDeSaude[] = [];
  const ativa = company.status === 'ativa';
  const implantando = company.status === 'onboarding';
  const add = (id: string, tom: TomDoAchado, texto: string, aba: AchadoDeSaude['aba']): void => {
    achados.push({ id, tom, texto, aba });
  };

  if (company.status === 'pausada') {
    add('pausada', 'neutral', 'Conta pausada: nenhum checkout abandonado novo entra na régua.', null);
  }

  // Integracoes: o que faz a regua perder lead agora.
  if (setup.connectedProviders.length === 0) {
    add('sem-provedor', implantando ? 'warning' : 'critical', 'Nenhum provedor de checkout conectado: nenhum abandono chega ao motor.', 'integracoes');
  }
  if (setup.providersMissingSecret.length > 0) {
    const nomes = listaDeProvedores(setup.providersMissingSecret);
    const plural = setup.providersMissingSecret.length > 1;
    add('sem-segredo', 'critical', `${nomes} ${plural ? 'conectados' : 'conectado'} sem segredo: todo evento volta 401 e nenhum lead entra na régua.`, 'integracoes');
  }
  if (setup.whatsapp === 'disconnected') {
    add(
      'whatsapp',
      ativa ? 'critical' : 'warning',
      ativa ? 'WhatsApp desconectado: os toques de WhatsApp da régua não saem.' : 'WhatsApp ainda não conectado: falta ler o QR code da instância.',
      'integracoes',
    );
  }
  if (setup.voice === 'pending') {
    add('voz', 'warning', 'Assistente de voz aguardando configuração: a ligação da IA 30 min após o abandono não acontece.', 'integracoes');
  }
  if (ativa && setup.connectedProviders.length > 0) {
    if (setup.lastWebhookAt === null) {
      add('nunca-webhook', 'critical', 'Nenhum webhook recebido até hoje: confira URL e segredo no provedor.', 'integracoes');
    } else {
      const dias = diasDesde(setup.lastWebhookAt, agora.toISOString());
      if (dias >= DIAS_INTEGRACAO_MUDA) {
        add('integracao-muda', 'warning', `Nenhum webhook há ${descreverDias(dias)}: sem abandono chegando não há o que recuperar.`, 'integracoes');
      }
    }
  }

  // Resultado do periodo.
  if (ativa && metrics.abandonedCount > 0 && metrics.recoveredCount === 0) {
    add('sem-venda', 'warning', `Nenhuma venda recuperada no período, com ${formatCount(metrics.abandonedCount)} checkouts abandonados na régua.`, 'resumo');
  }
  if (previous !== null && ativa) {
    const v = variar(metrics.recoveredAmountCents, previous.recoveredAmountCents);
    if (v.relativaBps !== null && v.relativaBps <= QUEDA_RELEVANTE_BPS) {
      add('queda', 'warning', `Recuperado caiu ${formatPercentFromBps(Math.abs(v.relativaBps))} contra o período anterior.`, 'resumo');
    } else if (v.relativaBps !== null && v.relativaBps >= ALTA_RELEVANTE_BPS) {
      add('alta', 'good', `Recuperado subiu ${formatPercentFromBps(v.relativaBps)} contra o período anterior.`, 'resumo');
    }
    if (previous.abandonedCount > 0 && metrics.abandonedCount > 0 && previous.recoveryRateBps - metrics.recoveryRateBps >= QUEDA_DE_TAXA_BPS) {
      add('taxa', 'warning', `Taxa de recuperação caiu de ${formatPercentFromBps(previous.recoveryRateBps)} para ${formatPercentFromBps(metrics.recoveryRateBps)}.`, 'resumo');
    }
  }

  // Funis e equipe: oportunidade e coerencia, sempre depois do fato.
  const expiradosLigado = company.funnels.includes('expirados');
  if (expiradosLigado && setup.recurringOffers === 0) {
    add('expirados-sem-recorrente', 'warning', 'Funil de expirados ligado sem oferta recorrente: não há assinatura vencida para recuperar.', 'ofertas');
  }
  if (!expiradosLigado && setup.recurringOffers > 0 && company.status !== 'pausada') {
    add(
      'expirados-desligado',
      'neutral',
      `${formatCount(setup.recurringOffers)} ${setup.recurringOffers === 1 ? 'oferta recorrente' : 'ofertas recorrentes'} e o funil de expirados desligado: assinaturas que já venceram ficam sem régua.`,
      'resumo',
    );
  }
  if (ativa && setup.salesReps === 0) {
    add('sem-vendedora', 'neutral', 'Sem vendedora cadastrada: quem responde à régua cai no WhatsApp do comercial sem ninguém para registrar a venda assistida.', 'equipe');
  }

  achados.sort((a, b) => ORDEM[a.tom] - ORDEM[b.tom]);
  const passos = passosDeImplantacao(setup);

  let nivel: NivelSaude;
  let nota: number;
  if (company.status === 'pausada') {
    nivel = 'pausada';
    nota = Math.max(0, 100 - achados.reduce((acc, a) => acc + PESO[a.tom], 0));
  } else if (implantando) {
    nivel = 'implantacao';
    nota = Math.round((passos.filter((p) => p.feito).length * 100) / Math.max(1, passos.length));
  } else {
    nota = Math.max(0, 100 - achados.reduce((acc, a) => acc + PESO[a.tom], 0));
    nivel = achados.some((a) => a.tom === 'critical') ? 'critica' : achados.some((a) => a.tom === 'warning') ? 'atencao' : 'saudavel';
  }

  return { nivel, nota, achados, passos };
}

/** Primeiro achado que pede acao (critico ou atencao), para a linha curta da lista. */
export function achadoPrincipal(saude: SaudeDaEmpresa): AchadoDeSaude | null {
  return saude.achados.find((a) => a.tom === 'critical' || a.tom === 'warning') ?? null;
}
